import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canceler } from "axios";
import { useAppTable, useFormContainerState, useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    Button,
    VStack,
    HStack,
    Divider,
    Center,
    Text,
    Hide,
    useToast,
    useDisclosure,
    Box,
    ButtonGroup,
} from "@chakra-ui/react";
import { updatePageIndex } from "ka-table/actionCreators";
import { AppSearch } from "../../../containers";
import {
    AppAlertDelete,
    AppFilterButton,
    AppIcon,
    AppLoader,
    AppPageContent,
    AppPageHeader,
} from "../../../components";
import { InternshipList as ListComponent } from "./InternshipList";
import { InternshipForm as FormComponent } from "./InternshipForm";
import { InternshipFilter as FilterComponent } from "./InternshipFilter";
import {
    Internship as Entity,
    InternshipApi as EntityApi,
} from "../../../apis";
import { APPC, cPath } from "../../../config";

const AppInternshipScheduler = lazy(
    () =>
        import(
            "../../../containers/AppInternshipScheduler/AppInternshipScheduler"
        )
);

export const InternshipPage = () => {
    // hook
    const { t } = useTrans();
    const { user, grant } = useAuthData();
    const toast = useToast();
    const navigate = useNavigate();
    const formContainerState = useFormContainerState();
    const deleteAlertState = useDisclosure();

    // state & const
    const [search, setSearch] = useState("");
    const [filterData, setFilterData] = useState<any>({});
    const [loadingList, setLoadingList] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const [actionSelectId, setActionSelectId] = useState(0);
    const [isScheduleView, setIsScheduleView] = useState(true);
    const [isScheduleDetailPage, setIsScheduleDetailPage] = useState(false);

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const {
        table,
        pageIndex,
        pagesCount,
        pageSize,
        sortColumn,
        sortOrder,
        setPagesCount,
    } = useAppTable("name");

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    // handler
    const fetchListData = async () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            ...filterData,
        };
        params[`order[${sortColumn}]`] = sortOrder;
        if (search) {
            params["name"] = search;
        }

        if (grant.isInternshipSupervisor) {
            params["service.id"] = user?.companyServices?.map(
                (service) => service.id
            );
        }

        await EntityApi.getCollectionMyInternships<Entity>(
            pageIndex + 1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    setList(response.items);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const actionClickHandler = (key: string, data?: Entity) => {
        if (!data) {
            return;
        }
        switch (key) {
            case "edit":
                setIsScheduleDetailPage(false);
                formContainerState.open(data?.id);
                break;
            case "delete":
                setActionSelectId(data?.id);
                deleteAlertState.onOpen();
                break;
            case "internshipSchedule":
                navigate(
                    cPath("ADMIN.INTERNSHIP_SCHEDULE_PAGE", {
                        id: data?.id,
                    })
                );
                break;
            case "scheduleDetail":
                setIsScheduleDetailPage(true);
                formContainerState.open(data?.id);
                break;
        }
    };

    const deleteHandler = async () => {
        if (actionSelectId < 1) {
            return;
        }
        setLoadingList(true);
        await EntityApi.deleteItem(actionSelectId)
            .then(({ errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else {
                    fetchListData();
                }
            })
            .finally(() => setLoadingList(false));
    };

    // useEffect
    useEffect(() => {
        if (!formContainerState.isOpen && !isScheduleView) {
            fetchListData();
        }
    }, [
        pageIndex,
        pageSize,
        sortColumn,
        sortOrder,
        search,
        filterData,
        formContainerState.isOpen,
        isScheduleView,
    ]);

    useEffect(() => {
        if (pageIndex > 0) {
            table.dispatch(updatePageIndex(0));
        }
    }, [filterData]);

    return (
        <>
            <AppPageHeader title={t("padm.InternshipPage:text.pageTitle")}>
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <ButtonGroup size="sm" isAttached variant="outline">
                        <Button
                            className="btn-icon-md"
                            size="md"
                            isActive={isScheduleView}
                            onClick={() => setIsScheduleView(true)}
                        >
                            <AppIcon name="icl-calendar" w="1.25rem" />
                        </Button>
                        <Button
                            className="btn-icon-md"
                            size="md"
                            isActive={!isScheduleView}
                            onClick={() => setIsScheduleView(false)}
                        >
                            <AppIcon name="icl-list" w="1.25rem" />
                        </Button>
                    </ButtonGroup>
                    <Center height="2.1875rem">
                        <Divider
                            orientation="vertical"
                            borderColor={"var(--chakra-colors-greyT80)"}
                        />
                    </Center>
                    <AppSearch
                        onSearch={(value) => {
                            setSearch(value);
                            if (pageIndex > 0) {
                                table.dispatch(updatePageIndex(0));
                            }
                        }}
                    />
                    <FilterComponent
                        filterData={filterData}
                        setFilterData={setFilterData}
                    >
                        <Box role="button">
                            <AppFilterButton filterData={filterData} />
                        </Box>
                    </FilterComponent>
                    {(grant.isSchoolAndCompany ||
                        grant.isInternshipCoordinator ||
                        grant.isInternshipSupervisor) && (
                        <>
                            <Center height="2.1875rem">
                                <Divider
                                    orientation="vertical"
                                    borderColor={"var(--chakra-colors-greyT80)"}
                                />
                            </Center>
                            <Button
                                variant={"primary"}
                                onClick={() => formContainerState.open(0)}
                            >
                                <AppIcon name="ics-plus-circle" w="1rem" />
                                <Hide below="md">
                                    <Text ml={2}>
                                        {t("padm.InternshipPage:button.create")}
                                    </Text>
                                </Hide>
                            </Button>
                        </>
                    )}
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <>
                    {isScheduleView && (
                        <Suspense fallback={<AppLoader />}>
                            <AppInternshipScheduler
                                filterData={
                                    search
                                        ? { ...filterData, name: search }
                                        : filterData
                                }
                                onActionClick={actionClickHandler}
                            />
                        </Suspense>
                    )}
                    <VStack p={6}>
                        {!isScheduleView && (
                            <ListComponent
                                table={table}
                                paging={paging}
                                list={list}
                                loadingList={loadingList}
                                onActionClick={actionClickHandler}
                            />
                        )}
                        {deleteAlertState.isOpen && (
                            <AppAlertDelete
                                isOpenDelete={deleteAlertState.isOpen}
                                deleteHandler={() => deleteHandler()}
                                onCloseDelete={deleteAlertState.onClose}
                            />
                        )}
                    </VStack>
                    {formContainerState.isOpen && (
                        <FormComponent
                            formContainerState={formContainerState}
                            isScheduleDetailPage={isScheduleDetailPage}
                        />
                    )}
                </>
            </AppPageContent>
        </>
    );
};
